import React from 'react';
import { 
  Phone, 
  MoreVertical, 
  CalendarPlus, 
  UserPlus, 
  Trash2, 
  Mic, 
  Sparkles 
} from 'lucide-react';
import CallBadges from './CallBadges';

/**
 * CallListItem - Individual call log item with actions, recording, and AI summary
 */
const CallListItem = ({
  call,
  displayName,
  displayPhone,
  formattedDate,
  formattedTime,
  isDropdownOpen,
  onToggleDropdown,
  onCall,
  onNewMeeting,
  onAddContact,
  onDelete,
  onClick,
  voiceReady,
  outcomeInfo,
  className = ''
}) => {
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const isMissed = call.status === 'no-answer' || call.status === 'busy' || call.status === 'failed';
  const isCompleted = call.status === 'completed';

  return (
    <div
      onClick={(e) => {
        if (e.target.closest('button')) return;
        onClick?.(call);
      }}
      className={`bg-gray-900 border border-gray-400/20 rounded-lg p-3 md:p-4 hover:bg-gray-800 hover:border-gray-500 transition-all cursor-pointer ${className}`}
      data-testid={`call-log-${call.id}`}
    >
      <div className="flex items-start gap-2 md:gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
          <Phone size={24} className="text-gray-400" />
        </div>

        {/* Call Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-semibold text-white">{displayName}</h4>
              <CallBadges
                direction={call.direction}
                isAIHandled={call.vapi_handled || call.status === 'vapi_completed' || call.status === 'forwarded_to_vapi'}
                outcome={call.outcome || outcomeInfo}
                sentiment={call.sentiment}
                leadQuality={call.lead_quality}
              />
            </div>
            <div className="flex items-center gap-2">
              {/* Call Action */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onCall?.(call, displayPhone, displayName);
                }}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-full transition-colors"
                title={voiceReady ? "Call via browser" : "Call back"}
                data-testid={`call-btn-${call.id}`}
              >
                <Phone size={16} className="text-white" />
              </button>
              
              {/* 3-dot menu */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDropdown?.();
                  }}
                  className="p-2 hover:bg-gray-700 rounded-full transition-colors"
                  title="More options"
                  data-testid={`call-menu-${call.id}`}
                >
                  <MoreVertical size={16} className="text-gray-400" />
                </button>
                
                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-gray-800 border border-gray-400/20 rounded-lg shadow-xl z-50">
                    <div className="py-1">
                      <button
                        onClick={() => onNewMeeting?.(displayName, displayPhone)}
                        className="w-full px-4 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <CalendarPlus size={16} />
                        New Meeting
                      </button>
                      <button
                        onClick={() => onAddContact?.(displayName, displayPhone)}
                        className="w-full px-4 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                      >
                        <UserPlus size={16} />
                        Add Contact
                      </button>
                      <div className="border-t border-gray-400/10 my-1"></div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete?.(call);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-gray-700 transition-colors flex items-center gap-2"
                        data-testid={`delete-call-${call.id}`}
                      >
                        <Trash2 size={16} />
                        Delete Call
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
            <span>{displayPhone}</span>
            <span>•</span>
            <span>{formattedDate} at {formattedTime}</span>
          </div>

          {/* Call Duration or Status */}
          <div className="flex items-center gap-2 text-xs">
            {isCompleted && call.duration && (
              <span className="text-gray-400">
                Duration: {formatDuration(call.duration)}
              </span>
            )}
            {isCompleted && !call.duration && (
              <span className="text-green-400 font-medium">
                Completed
              </span>
            )}
            {isMissed && (
              <span className="text-red-400 font-medium">
                {call.status === 'no-answer' ? 'No Answer' : call.status === 'busy' ? 'Busy' : 'Failed'}
              </span>
            )}
            {call.status === 'ringing' && (
              <span className="text-yellow-400 font-medium">
                Ringing...
              </span>
            )}
            {call.status === 'in-progress' && (
              <span className="text-green-400 font-medium animate-pulse">
                In Progress
              </span>
            )}
          </div>

          {/* Recording Link */}
          {call.recording_url && (
            <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Mic size={14} className="text-purple-400" />
                <span className="text-xs font-semibold text-purple-400">CALL RECORDING</span>
              </div>
              <a 
                href={call.recording_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                <Mic size={12} />
                Play Recording
              </a>
            </div>
          )}

          {/* AI Summary Section */}
          {call.ai_summary && (
            <div className="mt-3 p-3 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-purple-400" />
                <span className="text-xs font-semibold text-purple-400">AI SUMMARY</span>
                {/* Sentiment & Lead Quality Badges */}
                <div className="flex items-center gap-1.5 ml-auto">
                  {call.sentiment && (
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                      call.sentiment === 'positive' ? 'bg-green-500/20 text-green-400' :
                      call.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {call.sentiment === 'positive' ? '😊' : call.sentiment === 'negative' ? '😟' : '😐'} {call.sentiment}
                    </span>
                  )}
                  {call.lead_quality && (
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                      call.lead_quality === 'hot' ? 'bg-orange-500/20 text-orange-400' :
                      call.lead_quality === 'warm' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {call.lead_quality === 'hot' ? '🔥' : call.lead_quality === 'warm' ? '☀️' : '❄️'} {call.lead_quality}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Summary Text */}
              <p className="text-xs text-gray-300 leading-relaxed line-clamp-2 mb-2">
                {call.ai_summary}
              </p>
              
              {/* Action Items */}
              {call.action_items && call.action_items.length > 0 && (
                <div className="mt-2 pt-2 border-t border-purple-500/20">
                  <p className="text-[10px] font-semibold text-purple-400 uppercase mb-1">Action Items</p>
                  <ul className="space-y-0.5">
                    {call.action_items.slice(0, 2).map((item, idx) => (
                      <li key={idx} className="text-[11px] text-gray-400 flex items-start gap-1.5">
                        <span className="text-purple-400 mt-0.5">•</span>
                        <span className="line-clamp-1">{item}</span>
                      </li>
                    ))}
                    {call.action_items.length > 2 && (
                      <li className="text-[10px] text-purple-400">
                        +{call.action_items.length - 2} more...
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallListItem;
